// Contrato de la planta editable. Lo escribe el importador que lee el PDF de
// Archicad (arquitectura-ad/modelo-salamanca/importar.py); acá no se escribe a
// mano ninguna geometría, así sirve para los 60+ modelos del catálogo.
//
// Todas las medidas salen en METROS, relativas a la esquina superior izquierda
// de la caja de la planta. El importador entrega puntos PDF; `desdeImportador`
// hace la conversión una sola vez.

// El catálogo de mobiliario es quien decide qué pieza es cada bulto que trae el
// plano: acá sólo se convierte de puntos a metros. Ver `reconocer`.
import { reconocer } from './muebles';

/** Orientación de una pared. Las plantas del catálogo son ortogonales. */
export type Orientacion = 'v' | 'h';

/**
 * Un rectángulo en metros: [x, y, ancho, alto].
 *
 * Vive acá y no en `medirPlanta` —que es quien más lo usa— porque el contrato lo
 * necesita: una `Libre` levantada con la varita guarda su forma en estos. La
 * medición lo re-exporta para no tocar los imports que ya lo traen de ahí.
 */
export type Rect = [number, number, number, number];

/**
 * Qué llena un tramo vacío de pared.
 *
 * `paso` es el hueco pelado y es el que trae la importación: el PDF muestra
 * dónde la pared se interrumpe, pero la extracción no distingue una puerta de
 * una ventana de un paso franco. Nombrarlo `paso` en vez de adivinar «puerta»
 * es lo que deja la lámina importada dibujada igual que antes, y el tipo se
 * pone a dedo cuando alguien lo sabe.
 */
export type TipoVano = 'paso' | 'puerta' | 'ventana';

/**
 * Un tramo vacío de la pared: puerta, ventana o paso franco.
 *
 * Se dibuja, no se mide: la inundación tapa el vano a propósito, así que abrir
 * una puerta NO junta dos cuartos en uno para efectos de área (ver
 * `medirPlanta`). `a` y `b` van en las mismas coordenadas a lo largo que las de
 * la pared que lo contiene, y el ancho es la diferencia.
 *
 * El `id` es local a su pared y sirve para una sola cosa: que la selección
 * sobreviva a las operaciones que rehacen la lista de vanos —partir, unir,
 * recortar una punta—, donde el índice se corre y se terminaba editando el vano
 * de al lado.
 */
export interface Vano {
  id: number;
  a: number;
  b: number;
  tipo: TipoVano;
  /**
   * Estado de rehabilitación. Ausente = `existente`. Ver `Estado`.
   *
   * Lo decidió Daniel el 10-oct-2026 contra la alternativa de no tenerlo: abrir
   * una puerta en un muro que se queda de pie es el gesto más común de una
   * remodelación, y sin esto había que mentirlo marcando el muro ENTERO como
   * nuevo —que es decir que se tumba y se vuelve a levantar—.
   *
   * Un vano que la vista no muestra deja la pared CERRADA ahí, que es
   * exactamente lo que se quiere ver en «Estado actual»: la puerta todavía no
   * existe. No cambia ninguna área —la inundación tapa los vanos a propósito,
   * ver `medirPlanta`— así que esto es dibujo puro, y es el único estado del que
   * se puede decir eso con seguridad.
   */
  estado?: Estado;
}

/**
 * Una línea que parte un área en dos SIN ser pared.
 *
 * Existe porque un cuarto y su nombre no siempre coinciden con lo que las
 * paredes encierran: la planta baja de casi todos los modelos es un solo
 * espacio continuo, y «Sala-Comedor» son dos cuartos para quien vende aunque
 * sean uno para la geometría. Poner un tabique falso para separarlos mentiría
 * sobre la casa; esto no.
 *
 * Frena la inundación POR LA ARISTA, no ocupando celdas: por eso no tiene
 * espesor y las dos mitades suman exactamente lo que sumaba el área entera
 * (ver `medirPlanta`). Se dibuja punteado, para que se lea como lo que es —una
 * división de uso— y nunca se confunda con un muro.
 *
 * Va de punta a punta libre: el trazo no se obliga a ortogonal porque un
 * comedor en diagonal es tan legítimo como uno recto.
 */
export interface Divisor {
  id: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /**
   * Estado de rehabilitación. Ausente = `existente`. Ver `Estado`.
   *
   * Mide, aunque no sea pared: frena la inundación por la arista, así que un
   * divisor que la vista no muestra deja de partir el área en dos.
   */
  estado?: Estado;
}

/**
 * Una cota puesta a mano: dos puntos y el número que los separa.
 *
 * Las que acotan las paredes NO viven acá: se DERIVAN de las paredes en cada
 * repintado (ver `cotasAuto` en EditorPlanta). Esa es la decisión de fondo del
 * pedido de Daniel del 5-oct-2026 —«que acote en automático todas las paredes
 * que se vean existentes a centro de pared»— y la razón es que una cota
 * guardada sería una foto: al estirar la pared el dibujo diría 3,40 donde el
 * plano ya mide 3,85, y una cota que miente es peor que ninguna. Derivadas,
 * siguen a la pared sola.
 *
 * Esta estructura es para la OTRA mitad del pedido: «que esté la función de
 * agregar nuevas cotas, nuevas dimensiones». Son las que no cuelgan de una
 * pared —un retiro, un ancho de paso, la distancia a un lindero— y por eso sí
 * hay que guardarlas.
 *
 * Opcional en `Planta` por lo mismo que `divisores`: una planta guardada antes
 * de que esto existiera tiene que seguir abriendo.
 */
export interface Cota {
  id: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /**
   * Cuánto se SEPARA la línea de cota de lo que mide, en metros y con signo,
   * medido en la perpendicular del segmento. Ausente o cero = pegada, que es
   * como nacieron todas antes del 22-oct-2026.
   *
   * Es un solo escalar y no un punto libre, y esa es la decisión de fondo: en
   * un plano la cota se despega PERPENDICULAR a lo que mide y se une a los dos
   * puntos por sus testigos. Dejarla volar a cualquier lado la desalinearía de
   * lo medido y el dibujo dejaría de decir qué mide — que es justo lo que una
   * cota existe para decir. Con un escalar, arrastrarla no puede mentir: el
   * número no cambia y los testigos siguen señalando los mismos dos puntos.
   *
   * Lo pidió Daniel el 22-oct-2026: «que las cotas tengan la posibilidad de uno
   * poder moverlas, que no siempre estén pegadas hacia la pared […] uno pueda
   * arrastrarla […] y así uno la puede jalar pues a donde desea». Y en la misma
   * frase separó las dos mitades: «se puede editar los nodos de dónde está
   * midiendo, y el otro es el arrastre […] de que la cota esté separada de
   * dónde está midiendo». Los nodos son `x1,y1`/`x2,y2`; la separación es esto.
   *
   * El signo decide de qué lado cae, y por eso el arrastre puede cruzar al otro
   * lado sin un modo aparte: pasar por cero y seguir.
   */
  off?: number;
  /** Estado de rehabilitación. Ausente = `existente`. Ver `Estado`. */
  estado?: Estado;
}

/**
 * De qué lado suma un área.
 *
 * `interna` es lo que entra al ÁREA INTERNA del cajetín —la que se compara
 * contra el modelo y cuya diferencia fija el precio del extra—; `externa` es lo
 * que se mide y se muestra pero va aparte: terraza, pilas, cochera.
 *
 * Lo pidió Daniel el 4-oct-2026: «que uno pueda categorizar las áreas como área
 * interna o área externa […] para que a la hora de sumar pueda sumarse por área
 * interna y área externa». Antes la categoría estaba IMPLÍCITA en el tipo de
 * objeto —toda región cerrada contaba adentro, toda área dibujada a mano
 * afuera— y eso no alcanza: una cochera techada y cerrada por paredes es una
 * región cerrada que no es área interna, y un cuarto que el papá agrega sobre
 * una terraza existente sí lo es. La categoría pasa a ser dato del usuario, con
 * esos dos defaults de arranque.
 *
 * El 7-oct-2026 pasó de dos valores a CUATRO —«Interna · Externa · Lote ·
 * Azotea»—, y son cuatro columnas INDEPENDIENTES, no dos más dos que se
 * reparten entre las de antes. La razón es el precio: `interna` es la única que
 * alimenta `total`/`delta`, y meter el lote adentro de cualquiera de las dos
 * sumas viejas lo movería. Un lote de 90 m² sumado a `externa` no cambia el
 * precio pero sí la cifra que el vendedor lee como «área externa», y la azotea
 * es un caso aparte por lo mismo: se mide, se muestra y se cobra distinto.
 *
 * `lote` además es el único que el importador puede poner solo, leyendo el
 * verde del croquis (ver `importar.py`), y por eso nace bloqueado.
 */
export type Clase = 'interna' | 'externa' | 'lote' | 'azotea';

/** Las cuatro clases en el orden en que se leen y se muestran. */
export const CLASES: Clase[] = ['interna', 'externa', 'lote', 'azotea'];

/** Cómo se llama cada clase en pantalla. */
export const ROTULO_CLASE: Record<Clase, string> = {
  interna: 'Interna',
  externa: 'Externa',
  lote: 'Lote',
  azotea: 'Azotea',
};

/**
 * En qué estado de rehabilitación está un elemento: lo que ya estaba, lo que se
 * agrega y lo que se tumba.
 *
 * Lo pidió Daniel el 10-oct-2026: «poder poner cualquier elemento, ya sea una
 * pared, sea un área, sea algún objeto inmobiliario o demás, que se pueda
 * clasificar en estados de rehabilitación». Es un eje ORTOGONAL a `Clase`: un
 * área puede ser `interna` y `demoler` a la vez, y mezclarlos en una sola lista
 * obligaría a elegir entre decir de qué lado suma y decir si va a existir.
 *
 * Ausente = `existente`, y eso no es un default de conveniencia: una planta
 * importada de un PDF es, entera, lo que hoy está construido. Así los 50 modelos
 * ya publicados siguen abriendo y midiendo exactamente igual sin tocarles un
 * dato.
 */
export type Estado = 'existente' | 'nuevo' | 'demoler';

/** Los tres estados en el orden en que se leen: lo que hay, lo que viene, lo que se va. */
export const ESTADOS: Estado[] = ['existente', 'nuevo', 'demoler'];

/** Cómo se llama cada estado en pantalla. */
export const ROTULO_ESTADO: Record<Estado, string> = {
  existente: 'Existente',
  nuevo: 'Nuevo',
  demoler: 'A demoler',
};

/**
 * Qué se está mirando del proyecto.
 *
 * Arrancó con las cuatro combinaciones que pidió Daniel —«mostrar solo los de
 * demoler, mostrar solo los nuevos, mostrar los existentes con los nuevos y que
 * se oculte lo de demoler o que se muestre lo existente con los de demoler»—
 * más `todo`, sin lo cual no habría forma de revisar una planta ya marcada.
 *
 * El 10-oct-2026 Daniel las dobló: «el final sería existente + final y sin ver
 * demolido pero todo en negro sin que se vea azul … luego sería agregar estado
 * inicial que sería existente más demolición sin color rojo». Es decir: las dos
 * que describen una CASA van dos veces, una con la tinta de la remodelación y
 * otra limpia. El par `actual`/`inicial` enseña lo mismo (existente + a demoler)
 * y el par `planteado`/`final` también (existente + nuevo); lo único que cambia
 * dentro de cada par es si se tiñe —ver `VISTAS_SIN_TINTA`—.
 *
 * De las siete, sólo esas CUATRO son una casa de verdad. `nuevo` y `demoler` son
 * vistas de diagnóstico —sirven para contar y para pedir presupuesto, no
 * describen un edificio—, y `todo` superpone dos momentos que nunca coexisten.
 * Eso es lo que decide cuál manda para el ancla: ver `medirSegunVista`.
 */
export type VistaObra =
  'inicial' | 'actual' | 'planteado' | 'final' | 'nuevo' | 'demoler' | 'todo';

/**
 * Las vistas en el orden del botón, que es el de la obra: cómo está la casa,
 * qué se le tumba, qué se le levanta, cómo queda. Después las dos de conteo y
 * `todo`. La de entrada es `planteado` —ver `vistaObra` en el editor—: es la
 * única que muestra las dos marcas a la vez, y por eso es la de trabajar.
 */
export const VISTAS_OBRA: VistaObra[] = [
  'inicial', 'actual', 'planteado', 'final', 'nuevo', 'demoler', 'todo',
];

/** Cómo se llama cada vista en pantalla. */
export const ROTULO_VISTA: Record<VistaObra, string> = {
  inicial: 'Estado inicial',
  actual: 'Estado actual',
  planteado: 'Planteado',
  final: 'Estado final',
  nuevo: 'Obra nueva',
  demoler: 'Demoliciones',
  todo: 'Todo',
};

/**
 * Qué estados deja ver cada vista.
 *
 * La tabla es el contrato entero de la función: el dibujo y la medición leen de
 * acá y de ningún otro lado, así que no pueden desalinearse —que es exactamente
 * el defecto que tuvo el recorte de plantas cuando el color decidía una cosa y
 * la geometría otra—.
 */
export const ESTADOS_DE_VISTA: Record<VistaObra, Estado[]> = {
  inicial: ['existente', 'demoler'],
  actual: ['existente', 'demoler'],
  planteado: ['existente', 'nuevo'],
  final: ['existente', 'nuevo'],
  nuevo: ['nuevo'],
  demoler: ['demoler'],
  todo: ['existente', 'nuevo', 'demoler'],
};

/**
 * Las vistas que NO tiñen: ahí todo se dibuja en negro.
 *
 * El rojo y el azul son el lenguaje de la REMODELACIÓN —dicen qué se tumba y
 * qué se levanta—, y eso sólo tiene sentido mientras se está mirando el cambio.
 * `inicial` y `final` no muestran un cambio sino una casa en un momento, así
 * que salen como lo que son: un plano.
 */
export const VISTAS_SIN_TINTA: VistaObra[] = ['inicial', 'final'];

/** Si un elemento en ese estado se ve —y por lo tanto, se mide— en esa vista. */
export function seVe(estado: Estado | undefined, vista: VistaObra): boolean {
  return ESTADOS_DE_VISTA[vista].includes(estado ?? 'existente');
}

/**
 * Si dos vistas enseñan exactamente lo mismo y sólo se diferencian en la tinta.
 *
 * Existe para que los dos pares no se puedan desalinear: la medición ancla
 * contra una de las cuatro casas y el color no mide, así que preguntar por la
 * GEOMETRÍA es lo correcto —comparar con `'final'` a mano dejaría a `planteado`
 * recalculando lo mismo dos veces, y al revés—.
 */
export function mismaGeometria(a: VistaObra, b: VistaObra): boolean {
  const x = ESTADOS_DE_VISTA[a];
  const y = ESTADOS_DE_VISTA[b];
  return x.length === y.length && x.every((e) => y.includes(e));
}

/**
 * Un área dibujada a mano, que NO depende de que haya paredes alrededor.
 *
 * La pidió Daniel para lo que el plano no encierra pero la venta sí cobra: «el
 * área de pilas o de una terraza, que son áreas externas pero que son
 * importantes de poder visualizar». La inundación no las puede encontrar —no
 * hay nada que inundar— así que se dibujan y se miden por su propia geometría.
 *
 * Dibujada a mano es un rectángulo a propósito: pilas, terrazas y cocheras lo
 * son casi siempre y dos toques alcanzan para ponerla. Levantada con la varita
 * NO: ahí la forma la pone el plano y puede ser una L o una T (ver `celdas`).
 */
export interface Libre {
  id: number;
  nombre: string;
  /**
   * Esquina de arriba a la izquierda, y tamaño, en metros.
   *
   * Con `celdas` puesto esto es su CAJA —de dónde a dónde llega la forma— y no
   * su geometría: el área y el dibujo salen de las celdas. Se mantiene igual
   * porque es lo que ancla la paleta y el rótulo.
   */
  x: number;
  y: number;
  w: number;
  h: number;
  /**
   * La forma real, en rectángulos, cuando la levantó la varita.
   *
   * Ausente = el área ES su rectángulo, que es el caso de las dibujadas a mano.
   * Lo pidió Daniel el 4-oct-2026 al ver que la varita le devolvía un rectángulo
   * de 20,01 m² que se tragaba el W.C., el S.S. y la escalera: «no reconoció la
   * forma, como tuvo que haber cortado en varias partes». Un rectángulo no
   * puede decir la verdad sobre un espacio en L, así que la varita dejó de
   * entregar rectángulos.
   */
  celdas?: Rect[];
  /**
   * La forma real, en VÉRTICES ortogonales, cuando el usuario la editó o cuando
   * la trajo el verde del croquis.
   *
   * Es la tercera representación de un área y las tres se leen en orden: sin
   * `celdas` y sin `vertices` el área ES su rectángulo (lo dibujado a mano);
   * con `celdas` la forma son esos rectángulos (lo que levanta la varita, que
   * no se edita); con `vertices` la forma es este polígono, que SÍ se edita.
   *
   * Lo pidió Daniel el 7-oct-2026 por dos lados que son el mismo mecanismo:
   * «añadir posibilidad de dividir lados de áreas» y «desde el nodo de áreas se
   * debería poder cambiar la geometría del área para poder abarcar formas
   * irregulares». Tocar un lado le mete un vértice en el medio y cada mitad se
   * arrastra; con eso una L, una T o el lote de 7-8 vértices que trae el PDF se
   * dibujan sin salir del editor.
   *
   * ORTOGONALES a propósito: cada lado es horizontal o vertical, y arrastrar
   * mueve también el vértice vecino para mantenerlo. Un vértice libre daría
   * diagonales que ni las casas tienen ni el imán a paredes sabe seguir.
   *
   * El área sale por la fórmula del zapatero, así que el polígono puede ser
   * cóncavo sin que la medida se caiga.
   */
  vertices?: Array<[number, number]>;
  /**
   * El polígono admite ÁNGULOS CUALQUIERA: se edita vértice por vértice y sus
   * lados pueden ir en diagonal.
   *
   * Es el tercer método de modelar un área, pedido por Daniel el 7-oct-2026:
   * «el que hace falta es el polígono irregular… que sería una forma con
   * ángulos distintos, porque actualmente todo es como si fueran ángulos
   * ortogonales. Que pueden ser desde un triángulo o polígonos con ciertas
   * formas». Un chaflán, una esquina en ochavo o un lote en cuña no se pueden
   * decir con lados rectos.
   *
   * Viaja como bandera aparte y no como un `tipo` con cuatro valores porque lo
   * que cambia es UNA regla —si al mover se conserva la ortogonalidad— sobre la
   * misma representación: los dos siguen siendo `vertices`, se miden con la
   * misma fórmula del zapatero y se guardan igual. Ausente = ortogonal, que es
   * como nacieron todos los polígonos de antes de esta fecha.
   *
   * ⚠️ Un anillo con `anguloLibre` NO se puede partir en franjas horizontales
   * (`rectangulosDePoligono` descarta cualquier lado no vertical), así que se
   * pinta como `<polygon>` y su rótulo se ancla en el centroide. Lo único que
   * sigue viendo su caja es la barrera de la varita.
   */
  anguloLibre?: boolean;
  /**
   * De qué lado suma. Ausente = `externa`, que es para lo que nacieron: así una
   * planta guardada antes de que esto existiera sigue midiendo igual.
   */
  clase?: Clase;
  /**
   * Bloqueada: se ve, se selecciona y se le puede cambiar la clase, pero no se
   * mueve, no se redimensiona y no se borra.
   *
   * Lo pidió Daniel el 7-oct-2026 («añadir posibilidad de bloquear áreas») y es
   * lo que hace usable al lote: es el área más grande de la planta, está debajo
   * de todo lo demás, y sin candado cada toque que falla el borde de un cuarto
   * la agarra y la arrastra. Por eso el lote del importador NACE con esto
   * puesto.
   *
   * No se confunde con el candado del mueble, que es estado de pantalla y
   * devuelve agarres. Este es DATO: se guarda con la planta.
   */
  fijo?: boolean;
  /** Estado de rehabilitación. Ausente = `existente`. Ver `Estado`. */
  estado?: Estado;
}

/**
 * Un mueble puesto sobre la planta: un clóset, una cama, un módulo de cocina.
 *
 * Lo pidió Daniel el 4-oct-2026 con el argumento de fondo: «este tipo de casas
 * son muy repetitivos los módulos de mobiliario […] esto para mí tiene un montón
 * de potencial, ya que termina de amarrar todo el diseño».
 *
 * ⚠️ **Vive aparte de `libres` a propósito, y no es un detalle de orden.** Las
 * libres SUMAN al área (ver `libreInterna`/`libreExterna` en medirPlanta) y un
 * mueble no puede: está DENTRO de un cuarto cuya área ya lo incluye, así que
 * sumarlo contaría ese pedazo dos veces y movería el precio del extra. Por eso
 * `medirPlanta` no mira esta lista en ningún paso, y por eso tampoco bloquea la
 * varita ni la inundación.
 *
 * `w` y `h` son lo que el mueble OCUPA en el plano, ya resuelto el giro: girar
 * 90° los intercambia. El `giro` queda sólo para dibujar el símbolo mirando a
 * donde corresponde —el respaldo de la cama, el frente del inodoro—, porque un
 * rectángulo rotado es otro rectángulo pero una cama rotada no es otra cama.
 */
export interface Mueble {
  id: number;
  /** Clave del catálogo (`CATALOGO` en muebles.tsx). De ahí salen nombre y símbolo. */
  tipo: string;
  /** Esquina de arriba a la izquierda, y lo que ocupa, en metros. */
  x: number;
  y: number;
  w: number;
  h: number;
  /**
   * Hacia dónde mira el símbolo, en grados. Ausente = 0, que es el frente
   * hacia abajo (el sur de la pantalla).
   *
   * Sólo múltiplos de 90: en estas casas todo es ortogonal, y un ángulo libre
   * rompería el imán a caras de pared sin servirle a nadie.
   */
  giro?: 0 | 90 | 180 | 270;
  /**
   * Si el símbolo va volteado sobre su eje vertical propio.
   *
   * Lo pidió Daniel el 10-oct-2026, y el caso que lo obliga es el sofá en L:
   * existe en las dos manos y con sólo giros de 90° no se puede pasar de una a
   * la otra —girar lo acuesta, no lo invierte—. Es ortogonal al giro, así que
   * un mueble puede estar girado Y espejado.
   *
   * No toca la caja: `w`/`h` siguen siendo los mismos, y por eso no afecta al
   * imán de caras ni al reconocimiento. Es dibujo puro.
   */
  espejo?: boolean;
  /**
   * Radio de cada esquina, en metros, para el objeto «a medida» (el único del
   * catálogo marcado `flexible`). Orden `[no, ne, se, so]` —el de CSS, en
   * sentido del reloj desde arriba-izquierda— y en coordenadas LOCALES del
   * símbolo, no del plano: giran junto con el mueble, igual que el dibujo.
   *
   * Ausente = las cuatro en escuadra. Lo pidió Daniel el 5-oct-2026: «que uno
   * pueda hacerle, si uno desea, uno o más bordes redondeados, que al darle
   * clic uno a la esquina, salga el panel flotante en el que uno pueda ponerle
   * el radio que uno desea».
   *
   * ⚠️ No conviven con `vertices`: una esquina redondeada es un rasgo de las
   * CUATRO esquinas de un rectángulo, y un polígono de siete vértices no tiene
   * cuatro. Al darle forma al objeto se apagan. Ver `vertices`.
   */
  radios?: [number, number, number, number];
  /**
   * La forma real del objeto «a medida», en vértices y en coordenadas de MUNDO
   * —las mismas de `x`/`y`—, cuando el usuario se la dibujó.
   *
   * Ausente = el objeto ES su rectángulo, que es como nacen todos y como siguen
   * viviendo los del catálogo. Es la misma gradación que en `Libre.vertices`, y
   * a propósito: lo que Daniel pidió el 14-oct-2026 fue literalmente eso —«que
   * el objeto a medida tenga las mismas funciones que tiene el área en cuanto a
   * la posibilidad de poder dividirse, poder borrar un vértice, poder mover de
   * una manera con ángulos distintos»—, así que comparte las mismas rutinas
   * (`partirLado`, `quitarVertice`, `empujarLado`, `imantarVertice`) en vez de
   * tener unas paralelas que se separarían al primer arreglo.
   *
   * En MUNDO y no en coordenadas locales del símbolo porque con forma propia el
   * `giro` deja de tener trabajo: girar existe para orientar un dibujo fijo —un
   * inodoro, una cama— y acá el dibujo ES la forma, así que moverle un vértice
   * hace lo que el giro hacía, y mejor. Con `vertices` puesto, `giro` y `espejo`
   * se ignoran al pintar; `x`/`y`/`w`/`h` quedan como la CAJA, que es lo que
   * ancla la paleta y el imán de arrimar.
   *
   * Sólo lo lleva la pieza flexible (`CLAVE_A_MEDIDA`): el resto del catálogo
   * tiene un símbolo que dice qué es, y deformarlo lo convertiría en otra cosa
   * sin cambiarle el nombre.
   *
   * Y NO lleva un `anguloLibre` como el área: la forma del objeto admite
   * cualquier ángulo desde que nace. El modo ortogonal del área existe porque
   * ahí la forma se arma empujando lados enteros; acá lo que Daniel pidió fue
   * que la ortogonalidad AYUDE sin prohibir —«que por defecto trate siempre de
   * buscar una línea vertical u horizontal», con «la posibilidad de poder
   * moverlo hacia una dirección que sea diferente»—, y eso ya lo da el imán de
   * `imantarVertice`. Un modo aparte sería una segunda manera de decir lo
   * mismo, peor.
   */
  vertices?: Array<[number, number]>;
  /**
   * De qué cara de qué pared cuelga la pieza. Ausente = anda suelta por el
   * plano, que es como vive todo el mobiliario.
   *
   * Lo pidió Daniel el 9-oct-2026 para el eléctrico: «ciertos objetos, como son
   * tomacorrientes, apagadores, que tengan un comportamiento similar a las
   * puertas y ventanas, que se adhieren, pero en este caso se vería hacia el
   * lado externo o interno de la pared; que por defecto tenga ese
   * comportamiento, pero que si uno desea puede ponerlo en el centro de
   * cualquier área o en algún punto que uno defina que no sea la pared».
   *
   * Las dos mitades de esa frase son las dos ramas de este campo: con `ancla`
   * la pieza vive sobre la pared y `x`/`y` se RECALCULAN de ella —así el
   * tomacorriente sigue al muro cuando el muro se mueve, que es lo que uno
   * espera de algo adherido—; sin `ancla` manda `x`/`y` y la pieza se queda
   * donde se soltó.
   *
   * No es un `Vano`: el vano es un HUECO y vive dentro de `Pared.vanos` con su
   * `a`/`b`. Esto va ENCIMA de una de las dos caras y no abre nada.
   */
  ancla?: AnclaPared;
  /**
   * Altura de montaje sobre el piso terminado, en metros.
   *
   * Es lo que un plano eléctrico rotula al lado del símbolo (h=0,40 en un
   * tomacorriente corriente, h=1,10 sobre el mesón de la cocina). Daniel la
   * pidió el 9-oct-2026 «con altura por defecto según el tipo y editable en la
   * paleta»: el valor de fábrica sale del catálogo (`alturaDefecto`) y éste
   * sólo existe cuando alguien lo cambió.
   */
  altura?: number;
  /**
   * Estado de rehabilitación. Ausente = `existente`. Ver `Estado`.
   *
   * Acá el estado es PURAMENTE visual, y es el único caso en que lo es: la
   * medición no mira `muebles` en ningún paso —ver el comentario de arriba—, así
   * que tumbar un clóset esconde el símbolo y no mueve un centímetro de área.
   */
  estado?: Estado;
  /**
   * La medida con que esta pieza venía DIBUJADA en el plano, en metros.
   *
   * Sólo la traen las piezas que entraron por el importador, y no cambia nunca:
   * es lo que permite enseñarle al reconocedor qué es ese bloque cuando Daniel
   * lo reemplaza (ver `aprenderHuella`). `w`/`h` no sirven para eso porque el
   * reemplazo las pisa con las del catálogo —así que después del primer cambio
   * ya no son la huella del plano, son la de la pieza que entró—.
   */
  huella?: [number, number];
}

/**
 * Dónde se agarra una pieza que vive pegada a una pared.
 *
 * `t` va en las MISMAS coordenadas a lo largo que `Pared.a`/`b` —o sea `y` en
 * una pared vertical y `x` en una horizontal—, igual que `Vano.a`/`b`. Es el
 * centro de la pieza, no su esquina: una pieza se corre a su sitio mirando el
 * centro, y guardar la esquina obligaría a recalcular en cada giro.
 *
 * `cara` es el SIGNO sobre el eje de la pared: `-1` el lado de coordenada menor
 * (izquierda en una vertical, arriba en una horizontal) y `+1` el otro. Se
 * guarda el signo y no la palabra «interno» a propósito: cuál de las dos caras
 * mira a un recinto depende de la medición de la planta, que cambia cuando
 * alguien abre una pared, y una pieza no tiene por qué saltar de lado porque la
 * casa se reconfiguró. La palabra se calcula para MOSTRARLA —y para elegir el
 * lado al nacer—, pero lo que se guarda es geometría.
 */
export interface AnclaPared {
  pared: number;
  t: number;
  cara: -1 | 1;
}

/**
 * Una pared es un objeto SUELTO, no un eje que cruza el plano. Esto importa:
 * modelarla como eje convierte cualquier planta en un rectángulo con las áreas
 * equiparadas, que es como se arruinó el primer intento.
 *
 * `c` es la coordenada del eje (x si es vertical, y si es horizontal); `a` y `b`
 * son el principio y el fin a lo largo; `e` el espesor.
 */
export interface Pared {
  id: number;
  o: Orientacion;
  c: number;
  e: number;
  a: number;
  b: number;
  /** Tramos vacíos — puertas y ventanas. Se dibujan, no se miden. */
  vanos: Vano[];
  /**
   * Estado de rehabilitación. Ausente = `existente`. Ver `Estado`.
   *
   * Acá es donde el estado PESA: una pared que no se ve tampoco frena la
   * inundación, así que marcar un muro «a demoler» y mirar el estado final junta
   * los dos cuartos que separaba y el área lo dice. Es la mitad cara de la
   * función y fue decisión explícita de Daniel el 10-oct-2026.
   */
  estado?: Estado;
}

/** Rótulo de recinto leído del PDF. */
export interface Recinto {
  nombre: string;
  /**
   * El área que rotula el plano junto al nombre, o 0 si no rotula ninguna. Buena
   * parte de los croquis del catálogo (`CroquisBN-*.pdf`) rotulan SOLO el
   * nombre, así que queda en 0 seguido.
   *
   * Es INFORMATIVO: ya no corrige la medición. Desde que las áreas se miden a
   * ejes de pared el número sale bien solo, y sirve para ponerlo al lado —«a
   * ejes X · el plano rotula Y»— y que se pueda confiar en el nuevo. Ver
   * `anclar` en medirPlanta.ts para por qué la corrección se fue.
   */
  plano: number;
  x: number;
  y: number;
  /** Índice de la región cerrada donde cayó el rótulo. null = quedó al aire. */
  region: number | null;
}

export interface Planta {
  /** 1 = baja. El importador numera desde abajo. */
  n: number;
  nombre: string;
  paredes: Pared[];
  recintos: Recinto[];
  /**
   * Divisiones de uso: parten un área en dos sin poner pared.
   *
   * Opcional porque no las trae ninguna importación —las pone quien vende, al
   * separar «Sala» de «Comedor»—, y así una planta guardada antes de que esto
   * existiera sigue abriendo.
   */
  divisores?: Divisor[];
  /**
   * Áreas dibujadas a mano, fuera de lo que encierran las paredes: pilas,
   * terraza, cochera. Opcional por lo mismo que `divisores`.
   */
  libres?: Libre[];
  /**
   * El mobiliario puesto encima. Opcional por lo mismo que `divisores`, y
   * SEPARADO de `libres` porque no mide: ver el comentario de `Mueble`.
   */
  muebles?: Mueble[];
  /**
   * Las cotas puestas a mano. Las de las paredes se derivan y no se guardan:
   * ver el comentario de `Cota`.
   */
  cotas?: Cota[];
  /** Caja de la planta en puntos PDF: [x0, x1, y0, y1]. Es lo que permite
   *  poner el PDF de fondo alineado con las paredes. */
  caja: [number, number, number, number];
  /**
   * Hasta dónde llega el DIBUJO de la planta, en metros y relativo al mismo
   * origen que las paredes: `[x0, x1, y0, y1]`.
   *
   * Existe porque la vista se encuadraba con las paredes y nada más, y la
   * cochera no tiene paredes: quedaba fuera del `viewBox` y era inalcanzable.
   * Lo pidió Daniel el 5-oct-2026 —«estás cortando el dibujo de una manera que
   * queda incompleto, no se ve el área de garaje»—. Los números pueden ser
   * NEGATIVOS: el dibujo también se sale por arriba y por la izquierda.
   *
   * Ausente = encuadrar con las paredes, que es lo que hacían los modelos
   * medidos antes de que esto existiera.
   */
  marco?: [number, number, number, number];
  /**
   * Área cerrada de la planta según la empresa — `Modelos.AreaCCerrada1Nivel` /
   * `2Nivel` de AdelanteDB. Dato autorizado, existe para los 60+ modelos, y es
   * el MISMO que después consume `/costo`.
   *
   * Es INFORMATIVO, igual que `Recinto.plano`: el editor NO se ancla a él. La
   * medición se hace a ejes de pared —como calcula la empresa— y el absoluto que
   * sale puede no coincidir con el que quedó impreso en el cajetín; eso se
   * muestra al lado en vez de taparse. Decisión explícita de Daniel: «vamos con
   * ejes puros».
   */
  ancla: number | null;
  /** Se ancla una sola vez por planta, no por editor. */
  anclado: boolean;
  /** El área en reposo, congelada en el anclaje. La escribe `medirPlanta`. */
  referencia: number | null;
  /**
   * Área en reposo de cada región, por nombre. También la congela el anclaje.
   *
   * Se indexa por NOMBRE y no por índice de región porque los índices los asigna
   * la inundación y se renumeran en cuanto el usuario mueve una pared; el nombre
   * —los rótulos que caen adentro— es lo que identifica a una región entre dos
   * mediciones.
   */
  reposo: Record<string, number> | null;
  /**
   * De qué lado suma cada región, por nombre. Sólo guarda lo que el usuario
   * cambió: una región que no está acá es `interna`, que es lo que es una
   * región cerrada mientras nadie diga lo contrario.
   *
   * Por NOMBRE y no por índice por lo mismo que `reposo`: los índices los
   * reparte la inundación y se renumeran en cuanto alguien mueve una pared, así
   * que una cochera marcada como externa volvería a contar adentro al primer
   * movimiento.
   */
  clases?: Record<string, Clase>;
  /**
   * Regiones que el usuario mandó a contar como UNA, por nombre: la llave es el
   * nombre de la región y el valor el nombre del grupo.
   *
   * Es el inverso del `Divisor`. El divisor parte un espacio continuo en dos
   * usos; esto junta dos espacios que la geometría separó pero que para el que
   * vende son un solo cuarto —la sala con un tabique de 60 cm que no llega al
   * techo, el pasillo que la extracción cortó con una pared que el plano no
   * tiene—. No se puede resolver renombrando: `medirPlanta` numera los nombres
   * repetidos justamente para que el reposo no se pise, así que dos regiones con
   * el mismo nombre salen «Sala 1» y «Sala 2» y siguen siendo dos.
   *
   * Por NOMBRE y no por índice por lo mismo que `reposo` y `clases`.
   *
   * Unir NO cambia el área: el grupo suma lo que sumaban sus partes. Lo que
   * cambia es que pasan a tener un solo nombre, un solo reposo y un solo delta,
   * que es lo que se lee en la tabla y lo que fija el precio.
   */
  grupos?: Record<string, string>;
  /**
   * Regiones que no entran al cómputo, por nombre.
   *
   * Hace falta porque una región cerrada NO se puede borrar: no es un objeto del
   * modelo, la inventa la inundación en cada medición y volvería a aparecer al
   * instante. Esto es lo más cerca que hay de borrarla —desaparece de la tabla,
   * de las dos sumas y del delta— sin tocar una pared.
   *
   * No se confunde con la clase: `externa` SIGUE sumando, en la otra columna.
   * Esto no suma en ninguna. Es para lo que no es área de nada —el hueco de la
   * escalera contado dos veces, un patio de luz, lo que la extracción inventó—.
   */
  omitidas?: string[];
  /**
   * Estado de rehabilitación de cada REGIÓN CERRADA, por nombre.
   *
   * Vive acá y no en la región porque una región no es un objeto: la inventa la
   * inundación en cada medición y no tiene `id` que llevar el dato. Es el mismo
   * patrón ya probado de `reposo`, `clases` y `grupos`, y por el mismo motivo:
   * los índices los reparte la inundación y se renumeran en cuanto alguien mueve
   * una pared, así que un cuarto marcado «a demoler» volvería a existir al
   * primer movimiento. Decisión de Daniel el 10-oct-2026, sobre la alternativa
   * de heredarlo de las paredes que lo encierran —que adivina, y deja indefinido
   * cualquier cuarto rodeado de muros mixtos—.
   *
   * Sólo guarda lo que el usuario cambió: una región que no está acá es
   * `existente`.
   *
   * ⚠️ Una región oculta se comporta como `omitidas` —se va de la tabla, de las
   * cuatro columnas y del delta— y NO como una pared demolida: el espacio sigue
   * encerrado porque sus muros siguen de pie. Tumbar el cuarto de verdad es
   * marcar sus PAREDES.
   */
  estados?: Record<string, Estado>;
}

export interface ModeloPlanta {
  /** Nombre del PDF de origen, para trazabilidad. */
  pdf: string;
  /** Escala derivada del plano: puntos PDF por metro. */
  ptPorMetro: number;
  plantas: Planta[];
}

/**
 * Una planta vacía, para dibujar una casa que no viene de ningún PDF.
 *
 * Lo pidió Daniel el 5-oct-2026: «me gustaría poder crear un dibujo desde cero
 * […] que en la interfaz inicial donde se puede importar, exista opción de
 * arrancar en blanco». Es el editor entero sin lámina de fondo: las paredes se
 * trazan con el dedo, la varita levanta las áreas y la medición funciona igual
 * —nunca dependió del PDF, sólo de la geometría—.
 *
 * `ptPorMetro` queda en la escala corriente del catálogo (37,7953 pt/m = 1:75)
 * aunque no haya plano que escalar: el editor trabaja en metros y sólo la usa
 * para alinear el fondo, que acá no existe. Cero rompería esa división.
 */
export function enBlanco(nombre = 'Planta nueva'): ModeloPlanta {
  return {
    pdf: nombre,
    ptPorMetro: 37.7953,
    plantas: [{
      n: 1,
      nombre: 'Planta baja',
      paredes: [],
      recintos: [],
      caja: [0, 0, 0, 0],
      ancla: null,
      anclado: false,
      referencia: null,
      reposo: null,
    }],
  };
}

// ─────────────────────────────── lo que entrega el importador (puntos PDF) ──

interface ParedCruda {
  orient: Orientacion;
  c0: number;
  c1: number;
  a: number;
  b: number;
  /**
   * `[a, b]` o `[a, b, tipo]`. El tercer campo lo pone el reconocimiento de
   * carpintería del importador; los 32 modelos medidos antes traen parejas, y
   * por eso las dos formas tienen que entrar.
   */
  vanos: Array<[number, number] | [number, number, TipoVano]>;
}

/**
 * Un bulto de mobiliario que el plano ya trae dibujado, en puntos PDF.
 *
 * Sale de las capas de Archicad: `INT | Mobiliario`, `ARQ_MOBILIARIO`,
 * `MEC | Losa Sanitaria`. El importador junta la tinta pegada en un bulto y
 * manda el tamaño, la familia y el cuarto donde cayó; NO elige la pieza. Eso lo
 * hace `reconocer` en muebles.tsx, que es el dueño del catálogo.
 */
interface BultoCrudo {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  /** `mobiliario` o `sanitario`. */
  clase: string;
  /** El rótulo de recinto más cercano, o null. */
  cuarto: string | null;
  /**
   * La sigla celeste con que el plano nombra el aparato —`L`, `SC`, `R`…—, o
   * null si el bulto no tiene ninguna encima. Ver `SIGLA_PIEZA` en muebles.tsx.
   */
  sigla?: string | null;
  /**
   * Procedencia, sólo para diagnóstico: el nombre crudo de la capa de Archicad
   * de donde salió el bulto y cuántos trazos lo formaron. El editor no los
   * mira, pero cuando un objeto sale reconocido de más o de menos son lo que
   * dice de qué capa vino sin volver a abrir el PDF.
   */
  capa?: string;
  trazos?: number;
  /**
   * El lado en metros del anillo de desagüe que el importador halló adentro, o
   * null. Lo usa `reconocer` para separar el plato de ducha del inodoro, que
   * miden casi lo mismo; ver `Bulto` en muebles.tsx.
   */
  desague?: number | null;
}

interface RecintoCrudo {
  nombre: string;
  /** null cuando la lámina rotula el nombre pero no el área. */
  area: number | null;
  cx: number;
  cy: number;
}

interface PlantaCruda {
  n: number;
  caja: [number, number, number, number];
  /**
   * Hasta dónde hay que MOSTRAR la lámina, en puntos PDF, cuando el dibujo del
   * edificio sale de la caja. Ausente = la caja alcanza.
   *
   * Es otra caja a propósito. La `caja` decide qué masa se lee como pared y
   * dónde está el origen de las coordenadas; moverla cambiaría las paredes y
   * las áreas de los 32 modelos ya medidos. Esto no mide nada.
   */
  marco?: [number, number, number, number];
  /**
   * El terreno, en puntos PDF, cuando la lámina lo pinta de verde. Ausente o
   * `null` = la lámina no lo trae y la planta abre sin lote.
   *
   * `vertices` es su forma exacta, y es `null` cuando el verde del PDF no
   * resultó un anillo ortogonal (una esquina en chaflán): ahí sólo viaja la
   * caja, que es visiblemente más grande, y nunca un polígono con una diagonal
   * que el editor no puede sostener.
   */
  lote?: {
    x0: number; x1: number; y0: number; y1: number;
    vertices: Array<[number, number]> | null;
  } | null;
  paredes: ParedCruda[];
  recintos: RecintoCrudo[];
  /** El mobiliario que el plano ya trae dibujado. Ver `BultoCrudo`. */
  bultos?: BultoCrudo[];
  /**
   * Los totales que rotula el cajetín — `ÁREA CERRADA`, `A. INTERNA`. NO son
   * recintos: tienen la misma forma en el PDF, pero meterlos entre los cuartos
   * hace que la casa se cuente dos veces al anclar. Van aparte porque sirven
   * para dos cosas: son el ancla que trae el propio plano cuando el modelo de
   * la base todavía no tiene área, y son con lo que se verifica que la
   * extracción se parece a la casa.
   */
  totales?: Array<{ nombre: string; area: number }>;
}

export interface ModeloPlantaCrudo {
  pdf: string;
  pt_m: number;
  plantas: PlantaCruda[];
  /**
   * Cuántas parejas de cota concordaron con la escala elegida, sobre el total
   * leído. Es la CONFIANZA de la escala, y por eso viaja con el modelo.
   *
   * El importador ya rechaza por debajo del 35 %, así que acá sólo llegan
   * láminas aprobadas; pero 8 de 15 y 20 de 21 no son lo mismo, y quien revise
   * un plano importado tiene derecho a ver con qué firmeza se midió.
   */
  cota_ok?: number;
  cota_tot?: number;
}

/**
 * Cuánto puede desviarse del eje un lado del anillo del lote, en PUNTOS PDF.
 *
 * Es el mismo número que `ORTO_TOL` en `planta-tool/importar.py`, y tiene que
 * seguir siéndolo: allá decide qué anillo se acepta, acá decide qué anillo se
 * endereza. Si se separan, vuelve a entrar al editor un anillo que el
 * importador llamó ortogonal y el editor no.
 */
const ORTO_TOL_PT = 0.6;

/**
 * El anillo del PDF, llevado a ortogonal EXACTO.
 *
 * Nace de una pantalla en blanco (7-oct-2026): el importador acepta un lado
 * como vertical si se desvía menos de `ORTO_TOL` —0,6 pt, 1,6 cm— pero lo
 * escribe con la desviación adentro, y el editor exige ortogonalidad exacta
 * (1e-6) para partir el polígono en franjas. Los 0,12 pt de juego del lote de
 * Toledo —x 656,87 contra 656,75— dejaban `rectangulosDePoligono` sin UNA
 * franja, y de ahí para abajo: forma vacía, `forma[0]` indefinido, el editor
 * entero caído al prender la capa de áreas. 20 de los 50 modelos del catálogo.
 * El defecto de fondo no era el juego: eran dos definiciones de «ortogonal» a
 * cada lado del límite, y la que tolera siendo la que escribe.
 *
 * Se agrupa POR COORDENADA y no lado por lado: barrer el anillo en orden
 * arrastra el error acumulado hasta el último lado, que justo es el que cierra.
 * Las x que caen dentro de la tolerancia pasan a valer todas lo mismo —el
 * promedio del grupo— y con eso cada lado queda exactamente vertical u
 * horizontal, sin mover ningún vértice más de la tolerancia.
 *
 * Devuelve `null` si después de pegar sigue habiendo un lado con las dos
 * deltas grandes: eso es una diagonal de verdad —un chaflán— y ahí lo honesto
 * es entregar la caja, como ya decidía el importador.
 */
export function anilloOrtogonal(
  vs: Array<[number, number]>,
  tol = ORTO_TOL_PT,
): Array<[number, number]> | null {
  if (vs.length < 4) return null;
  // Cada valor al centro de su grupo. El grupo crece mientras el siguiente
  // valor esté a menos de `tol` del anterior.
  const pegar = (vals: number[]) => {
    const orden = [...new Set(vals)].sort((a, b) => a - b);
    const mapa = new Map<number, number>();
    for (let i = 0; i < orden.length;) {
      let j = i;
      while (j + 1 < orden.length && orden[j + 1] - orden[j] <= tol) j++;
      const grupo = orden.slice(i, j + 1);
      const centro = grupo.reduce((s, v) => s + v, 0) / grupo.length;
      for (const v of grupo) mapa.set(v, centro);
      i = j + 1;
    }
    return mapa;
  };
  const mx = pegar(vs.map(([x]) => x));
  const my = pegar(vs.map(([, y]) => y));
  const pegado = vs.map(([x, y]) => [mx.get(x)!, my.get(y)!] as [number, number]);
  // Los vértices que el pegado dejó encima de su vecino ya no son vértices:
  // su lado quedó de largo cero.
  const limpio = pegado.filter(([x, y], i) => {
    const [px, py] = pegado[(i + pegado.length - 1) % pegado.length];
    return Math.abs(x - px) > 1e-9 || Math.abs(y - py) > 1e-9;
  });
  if (limpio.length < 4) return null;
  for (let i = 0; i < limpio.length; i++) {
    const [ax, ay] = limpio[i];
    const [bx, by] = limpio[(i + 1) % limpio.length];
    if (Math.abs(ax - bx) > 1e-9 && Math.abs(ay - by) > 1e-9) return null;
  }
  return limpio;
}

/**
 * Convierte la salida del importador (puntos PDF) a metros de obra.
 *
 * `anclas` trae el área cerrada autorizada por número de planta — en AdVentas,
 * `{1: modelo.areaCCerrada1Nivel, 2: modelo.areaCCerrada2Nivel}`. Es opcional:
 * las láminas que sí rotulan área por recinto se anclan solas.
 */
export function desdeImportador(
  crudo: ModeloPlantaCrudo,
  anclas: Record<number, number | null | undefined> = {},
): ModeloPlanta {
  const k = 1 / crudo.pt_m;
  return {
    pdf: crudo.pdf,
    ptPorMetro: crudo.pt_m,
    plantas: crudo.plantas.map((pl) => {
      const [x0, , y0] = pl.caja;
      // El eje de una pared vertical se mide en x; el de una horizontal, en y.
      const origen = (o: Orientacion) => (o === 'v' ? x0 : y0);
      const largo = (o: Orientacion) => (o === 'v' ? y0 : x0);
      // El anillo del lote se endereza ACÁ, en puntos PDF y antes de escalar:
      // es el único lugar por donde pasa todo el catálogo ya publicado, así que
      // arreglarlo acá sana los 50 modelos sin volver a barrer los 183 croquis.
      // Ver `anilloOrtogonal` para por qué hace falta.
      const anilloLote = pl.lote?.vertices?.length
        ? anilloOrtogonal(pl.lote.vertices)
        : null;
      let idMueble = 0;
      return {
        n: pl.n,
        nombre: pl.n === 1 ? 'Planta baja' : `Planta ${pl.n}`,
        caja: pl.caja,
        marco: pl.marco && [
          (pl.marco[0] - x0) * k, (pl.marco[1] - x0) * k,
          (pl.marco[2] - y0) * k, (pl.marco[3] - y0) * k,
        ] as [number, number, number, number],
        // El mobiliario que el plano ya trae: el tamaño sale del PDF, la pieza
        // la elige el catálogo. Lo que no calza con nada cae en «a medida» con
        // su medida real, que es lo que el «reemplazar» de la paleta corrige.
        // Un bulto puede salir como VARIOS muebles: el croquis dibuja la cama y
        // su mesa de noche en un solo bloque. Por eso el id se lleva aparte y no
        // es el índice del bulto.
        muebles: (pl.bultos ?? []).flatMap((b) => {
          const w = (b.x1 - b.x0) * k;
          const h = (b.y1 - b.y0) * k;
          const piezas = reconocer({ w, h, clase: b.clase, cuarto: b.cuarto,
                                     sigla: b.sigla, desague: b.desague });
          return piezas.map((p) => ({
            id: ++idMueble,
            tipo: p.tipo,
            giro: p.giro,
            x: (b.x0 - x0) * k + p.x,
            y: (b.y0 - y0) * k + p.y,
            w: p.w,
            h: p.h,
            // `huella` es la medida del BLOQUE del plano congelada: el reemplazo
            // pisa `w`/`h` con las del catálogo y sin esto no quedaría qué
            // bloque era. Va la del bulto entero, no la de la parte, porque es
            // el bulto lo que se reconoce —y lo que Daniel corrige con un toque.
            huella: [w, h] as [number, number],
          }));
        }),
        // El lote entra como un ÁREA y no como un dibujo de fondo: Daniel lo
        // pidió como tipo de área («Interna, Externa, Lote, Azotea») justamente
        // para que mida y se vea en la tabla. Nace BLOQUEADO porque es el área
        // más grande de la planta y está debajo de todo: sin candado, cada toque
        // que falla el borde de un cuarto la agarra y la arrastra.
        libres: pl.lote ? [{
          id: 1,
          nombre: 'Lote',
          x: (pl.lote.x0 - x0) * k,
          y: (pl.lote.y0 - y0) * k,
          w: (pl.lote.x1 - pl.lote.x0) * k,
          h: (pl.lote.y1 - pl.lote.y0) * k,
          // Sin vértices el área ES su caja. El importador los manda en `null`
          // cuando el verde del PDF no cerraba un anillo ortogonal, y ahí la
          // caja es lo honesto: es visiblemente más grande que el terreno real.
          vertices: anilloLote
            ? anilloLote.map(([px, py]) =>
              [(px - x0) * k, (py - y0) * k] as [number, number])
            : undefined,
          clase: 'lote' as const,
          fijo: true,
        }] : undefined,
        ancla: anclas[pl.n] ?? null,
        anclado: false,
        referencia: null,
        reposo: null,
        paredes: pl.paredes.map((w, i) => ({
          id: i,
          o: w.orient,
          c: ((w.c0 + w.c1) / 2 - origen(w.orient)) * k,
          e: (w.c1 - w.c0) * k,
          a: (w.a - largo(w.orient)) * k,
          b: (w.b - largo(w.orient)) * k,
          // El tipo lo trae el reconocimiento de carpintería: la hoja a 45° del
          // plano es una puerta y el marco relleno es una ventana. Lo que llega
          // sin tipo —un hueco en la masa que no tiene carpintería dibujada, o
          // un modelo medido antes de que esto existiera— entra como `paso`,
          // que es lo honesto: hay hueco, no se sabe qué lo llena.
          vanos: w.vanos.map((v, j) => ({
            id: j + 1,
            a: (v[0] - largo(w.orient)) * k,
            b: (v[1] - largo(w.orient)) * k,
            tipo: v[2] ?? ('paso' as const),
          })),
        })),
        recintos: pl.recintos.map((r) => ({
          nombre: r.nombre,
          plano: r.area ?? 0,
          x: (r.cx - x0) * k,
          y: (r.cy - y0) * k,
          region: null,
        })),
      };
    }),
  };
}
